import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Avatar,
  Tabs,
  Tab,
  Chip,
  Divider,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { FaTrophy, FaMedal, FaCrown } from "react-icons/fa";
import { GiDiamondTrophy } from "react-icons/gi";
import img3 from "../../images/sega-coin.svg";
import HighRollers from "./HighRollers";
import { getAllData } from "../../Utility/API";
import { endPoints } from "../../constant/Environment";
import { setLoading } from "../../redux/Reducers/GlobalReducer/globalSlice";

const RANK_COLORS = {
  1: {
    bg: "linear-gradient(135deg, #FFD700 0%, #FF9F00 100%)",
    text: "#1a1a1a",
    glow: "#FFD700",
  },
  2: {
    bg: "linear-gradient(135deg, #E8E8E8 0%, #A8A8A8 100%)",
    text: "#1a1a1a",
    glow: "#C0C0C0",
  },
  3: {
    bg: "linear-gradient(135deg, #CD9B5B 0%, #8B4513 100%)",
    text: "#fff",
    glow: "#CD7F32",
  },
};

const AVATAR_COLORS = [
  "#e91e63",
  "#9c27b0",
  "#3f51b5",
  "#2196f3",
  "#00bcd4",
  "#009688",
  "#4caf50",
  "#ff5722",
  "#ff9800",
  "#795548",
  "#607d8b",
  "#f44336",
];

const PODIUM_CONFIG = {
  1: {
    height: 140,
    size: 72,
    icon: <FaTrophy size={22} color="rgba(0,0,0,0.3)" />,
  },
  2: {
    height: 110,
    size: 60,
    icon: <FaMedal size={18} color="rgba(0,0,0,0.2)" />,
  },
  3: {
    height: 90,
    size: 56,
    icon: <FaMedal size={16} color="rgba(255,255,255,0.3)" />,
  },
};

const formatGC = (val) => {
  if (val >= 1000000) return (val / 1000000).toFixed(1) + "M";
  if (val >= 1000) return (val / 1000).toFixed(1) + "K";
  return val.toString();
};

const getAvatarColor = (username) => {
  if (!username || username.length === 0) return AVATAR_COLORS[0];
  return AVATAR_COLORS[(username.charCodeAt(0) - 65) % AVATAR_COLORS.length];
};

const CoinDisplay = ({ amount, size = 12,currency }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
    {/* <img src={img3} alt="sc" style={{ width: size, height: size }} /> */}
    <Typography
      sx={{
        fontSize: size === 12 ? 13 : 11,
        fontWeight: 700,
        color: "#4eeccd",
      }}
    >
     + {formatGC(amount)} {currency}
    </Typography>
  </Box>
);

const PodiumCard = ({ player, position }) => {
  const { height, size, icon } = PODIUM_CONFIG[position];
  const rankColor = RANK_COLORS[position];
  const avatarColor = getAvatarColor(player.username);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: position === 1 ? 1.2 : 1,
      }}
    >
      {position === 1 && (
        <Box sx={{ mb: 0.5, animation: "pulse 2s infinite" }}>
          <FaCrown size={28} color="#FFD700" />
        </Box>
      )}

      <Box
        sx={{
          position: "relative",
          mb: 1,
          filter: `drop-shadow(0 0 12px ${rankColor.glow}60)`,
        }}
      >
        <Avatar
          sx={{
            width: size,
            height: size,
            fontSize: size * 0.4,
            fontWeight: 700,
            background: avatarColor,
            border: `3px solid ${rankColor.glow}`,
            boxShadow: `0 0 20px ${rankColor.glow}50`,
          }}
        >
          {player.username?.charAt(0) || "?"}
        </Avatar>
        <Box
          sx={{
            position: "absolute",
            bottom: -8,
            left: "50%",
            transform: "translateX(-50%)",
            background: rankColor.bg,
            borderRadius: "50%",
            width: 22,
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 11,
            fontWeight: 700,
            color: rankColor.text,
            border: "2px solid #0a0a0a",
          }}
        >
          {position}
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: "#fff",
          mt: 1.5,
          textAlign: "center",
          maxWidth: 90,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {player.username}
      </Typography>
      <Box sx={{ mt: 0.3 }}>
        <CoinDisplay amount={player.winnings} size={12} currency={player.currency} />
      </Box>
      <Box
        sx={{
          mt: 1,
          width: "85%",
          height,
          background: rankColor.bg,
          borderRadius: "8px 8px 0 0",
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          pt: 1,
          boxShadow: `0 -4px 20px ${rankColor.glow}30`,
        }}
      >
        {icon}
      </Box>
    </Box>
  );
};

const RankRow = ({ player, isCurrentUser }) => {
  const { t } = useTranslation();
  const rankColor = RANK_COLORS[player.rank];
  const avatarColor = getAvatarColor(player.username);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.2,
        borderRadius: 2,
        background: isCurrentUser
          ? "linear-gradient(90deg, rgba(78,236,205,0.12) 0%, rgba(3,179,73,0.08) 100%)"
          : "rgba(255,255,255,0.03)",
        border: `1px solid ${isCurrentUser ? "rgba(78,236,205,0.3)" : "transparent"}`,
        transition: "all 0.2s",
        "&:hover": {
          background: isCurrentUser
            ? "linear-gradient(90deg, rgba(78,236,205,0.18) 0%, rgba(3,179,73,0.12) 100%)"
            : "rgba(255,255,255,0.06)",
          borderColor: isCurrentUser
            ? "rgba(78,236,205,0.5)"
            : "rgba(255,255,255,0.08)",
        },
      }}
    >
      <Box sx={{ width: 32, textAlign: "center", flexShrink: 0 }}>
        {rankColor ? (
          <Box
            sx={{
              width: 28,
              height: 28,
              borderRadius: "50%",
              background: rankColor.bg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: 700,
              color: rankColor.text,
              mx: "auto",
            }}
          >
            {player.rank}
          </Box>
        ) : (
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 600,
              color: "rgba(255,255,255,0.5)",
            }}
          >
            #{player.rank}
          </Typography>
        )}
      </Box>

      <Avatar
        sx={{
          width: 36,
          height: 36,
          fontSize: 14,
          fontWeight: 700,
          background: avatarColor,
          flexShrink: 0,
        }}
      >
        {player.username?.charAt(0) || "?"}
      </Avatar>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          gap: 0.8,
        }}
      >
        <Typography
          sx={{
            fontSize: 13,
            fontWeight: 600,
            color: isCurrentUser ? "#4eeccd" : "#fff",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {player.username}
        </Typography>
        {isCurrentUser && (
          <Chip
            label={t("leaderboard.you")}
            size="small"
            sx={{
              height: 16,
              fontSize: 9,
              fontWeight: 700,
              background: "rgba(78,236,205,0.2)",
              color: "#4eeccd",
              border: "1px solid rgba(78,236,205,0.4)",
            }}
          />
        )}
      </Box>

      <CoinDisplay amount={player.winnings} currency={player.currency} />
    </Box>
  );
};

const Leaderboard = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tab, setTab] = useState(0);
  const [leaderboard, setLeaderboard] = useState([]);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      dispatch(setLoading(true));
      try {
        const response = await getAllData(endPoints.api.GET_LEADERBOARD);
        if (response.status === "success") {
          const rankedData = (response?.data ?? []).map((item, index) => ({
            ...item,
            rank: index + 1,
            username: item?.name,
            winnings: parseFloat(((item?.win ?? 0) - (item?.play ?? 0)).toFixed(2)),
          }));
          setLeaderboard(rankedData);
        }
      } catch (err) {
        console.log(err);
      } finally {
        dispatch(setLoading(false));
      }
    };
    fetchLeaderboard();
  }, [dispatch]);

  const podium =
    leaderboard.length >= 3
      ? [leaderboard[1], leaderboard[0], leaderboard[2]]
      : [];
  const listPlayers = leaderboard.slice(3);

  return (
    <Box sx={{ maxWidth: 820, mx: "auto", pb: 4 }}>
      <Box
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          background:
            "linear-gradient(135deg, #1a0533 0%, #0d1f3c 50%, #0a1a0a 100%)",
          border: "1px solid rgba(78,236,205,0.15)",
          boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
        }}
      >
        <Box sx={{ px: 3, pt: 2 }}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              minHeight: 40,
              "& .MuiTabs-indicator": {
                background: "linear-gradient(90deg, #4eeccd, #03B349)",
                height: 2,
                borderRadius: 1,
              },
              "& .MuiTab-root": {
                minHeight: 40,
                fontSize: 13,
                fontWeight: 600,
                color: "rgba(255,255,255,0.4)",
                textTransform: "none",
                px: 2,
                "&.Mui-selected": { color: "#4eeccd" },
              },
            }}
          >
            <Tab label={t("leaderboard.tabActive")} />
            <Tab label={t("leaderboard.tabHighRollers")} />
          </Tabs>
          <Divider sx={{ borderColor: "rgba(255,255,255,0.06)" }} />
        </Box>

        {tab === 0 && (
          <>
            <Box
              sx={{
                px: 3,
                pt: 3,
                pb: 2,
                background:
                  "linear-gradient(180deg, rgba(78,236,205,0.08) 0%, transparent 100%)",
                borderBottom: "1px solid rgba(255,255,255,0.06)",
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}
              >
                <GiDiamondTrophy size={28} color="#FFD700" />
                <Typography
                  sx={{
                    fontSize: { xs: 20, sm: 24 },
                    fontWeight: 800,
                    background:
                      "linear-gradient(90deg, #FFD700 0%, #FF9F00 50%, #4eeccd 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    letterSpacing: 0.5,
                  }}
                >
                  {t("leaderboard.title")}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 12, color: "rgba(255,255,255,0.4)" }}>
                {t("leaderboard.subtitle")}
              </Typography>
            </Box>

            {leaderboard.length >= 3 ? (
          <>
            <Box
              sx={{
                px: 3,
                pt: 3,
                pb: 0,
                display: "flex",
                alignItems: "flex-end",
                justifyContent: "center",
                gap: 1,
              }}
            >
              <PodiumCard player={podium[0]} position={2} />
              <PodiumCard player={podium[1]} position={1} />
              <PodiumCard player={podium[2]} position={3} />
            </Box>
            <Box sx={{ px: 2, pt: 2, pb: 1 }}>
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 0.8 }}
              >
                {listPlayers.map((player, index) => (
                  <RankRow
                    key={player.rank || index}
                    player={player}
                    isCurrentUser={false}
                  />
                ))}
              </Box>
            </Box>
          </>
            ) : (
              <Box sx={{ p: 4, textAlign: "center" }}>
                <Typography sx={{ color: "rgba(255,255,255,0.5)" }}>
                  {t("leaderboard.loading")}
                </Typography>
              </Box>
            )}
          </>
        )}

        {tab === 1 && <HighRollers />}
      </Box>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }`}</style>
    </Box>
  );
};

export default Leaderboard;
